import { Injectable, inject } from '@angular/core';
import {HttpClient} from '@angular/common/http';
import {Observable, ReplaySubject} from 'rxjs';
import {map, switchMap, first} from 'rxjs/operators';

import {OngoingRequestTrackerService, SessionService} from '@core';
import {BaseResult} from '@shared';

import {TimeWindowSuggestionsResult} from '../../models/time-window-suggestions-result.model';
import {GenerateTimeWindowSuggestionsResult} from '../../models/generate-time-window-suggestions-result.model';
import {HandleTimeWindowSuggestion} from '../../models/handle-time-window-suggestion.model';
import {TimeWindowSuggestion} from '../../models/time-window-suggestion.model';
import {StopOptimizationHttpParams} from '../../interceptors/stop-optimization-http-params';

@Injectable({
    providedIn: 'root'
})
export class TimeWindowSuggestionService {
    private readonly httpClient = inject(HttpClient);
    private readonly sessionService = inject(SessionService);
    private readonly ongoingRequestTracker = inject(OngoingRequestTrackerService);

    private timeWindowSuggestions$: ReplaySubject<TimeWindowSuggestion[]>;
    private readonly timeWindowSuggestionsCount$: ReplaySubject<number>;

    constructor() {
        this.timeWindowSuggestionsCount$ = new ReplaySubject(1);
    }

    public loadTimeWindowSuggestions(): void {
        this.timeWindowSuggestions$ = new ReplaySubject(1);
        this.generateTimeWindowSuggestions()
            .pipe(
                switchMap(() => this.requestTimeWindowSuggestions()),
                map(result => {
                    this.timeWindowSuggestionsCount$.next(result.timeWindowSuggestionsResponse.length);
                    return result.timeWindowSuggestionsResponse;
                })
            )
            .subscribe(this.timeWindowSuggestions$);
    }

    public generateTimeWindowSuggestions(): Observable<GenerateTimeWindowSuggestionsResult> {
        const url = `${this.sessionService.getApiUrl()}/time_window_suggestions/${this.sessionService.requestId}/30`;
        const requestOptions = this.ongoingRequestTracker.createTrackingEnabledRequestOptions();

        return this.httpClient.post<GenerateTimeWindowSuggestionsResult>(url, {}, requestOptions);
    }

    public getTimeWindowSuggestions(): Observable<TimeWindowSuggestion[]> {
        return this.timeWindowSuggestions$.asObservable().pipe(first());
    }

    public getTimeWindowSuggestionsCount(): Observable<number> {
        return this.timeWindowSuggestionsCount$.asObservable();
    }

    public setTimeWindowSuggestionsCount(count: number): void {
        this.timeWindowSuggestionsCount$.next(count);
    }

    public handleTimeWindowSuggestions(
        suggestionsToHandle: HandleTimeWindowSuggestion[],
        accept: boolean = false
    ): Observable<BaseResult> {
        const url = `${this.sessionService.getApiUrl()}/time_window_suggestions/${this.sessionService.requestId}`;
        const body = {
            acceptedSuggestions: accept ? JSON.parse(JSON.stringify(suggestionsToHandle)) : [],
            rejectedSuggestions: !accept ? JSON.parse(JSON.stringify(suggestionsToHandle)) : []
        };
        return this.httpClient.put<BaseResult>(url, body, {params: new StopOptimizationHttpParams(true).getHttpParams()});
    }

    private requestTimeWindowSuggestions(): Observable<TimeWindowSuggestionsResult> {
        const url = `${this.sessionService.getApiUrl()}/time_window_suggestions/${this.sessionService.requestId}`;
        return this.httpClient.get<TimeWindowSuggestionsResult>(url);
    }
}
